import { useState, useMemo, useCallback, useEffect } from 'react';
import { MagnifyingGlassIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import type { ClubTheme } from './themes';
import { useClubMembers, socialKeys } from './queries';
import { useAuth } from '@/lib/auth-context';
import { getUserProfile, updateUserProfile, checkUserHandleAvailable, type UserProfile } from '@/lib/coreApi';
import MemberCard from './MemberCard';
import ProfileEditor, { type UserProfileData } from '@/components/profiles/ProfileEditor';

interface MemberDirectoryProps {
  clubId: string;
  theme: ClubTheme;
  onMessageUser?: (userId: string) => void;
  onViewMember?: (userId: string) => void;
}

export default function MemberDirectory({ clubId, theme, onMessageUser, onViewMember }: MemberDirectoryProps) {
  const { session, profile: authProfile } = useAuth();
  const currentUserId = session?.user?.id;
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const { data: members = [], isLoading } = useClubMembers(clubId);
  const [showEditor, setShowEditor] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Load user's own profile for the "Your Profile" card
  useEffect(() => {
    if (!currentUserId) return;
    setProfileLoading(true);
    getUserProfile()
      .then((res) => setUserProfile(res.data))
      .catch(() => {})
      .finally(() => setProfileLoading(false));
  }, [currentUserId]);

  const handleSaveProfile = useCallback(async (updates: Record<string, unknown>) => {
    const res = await updateUserProfile(updates);
    setUserProfile(res.data);
    // Invalidate social queries so name/avatar changes propagate to posts + member list
    queryClient.invalidateQueries({ queryKey: socialKeys.members(clubId) });
    queryClient.invalidateQueries({ queryKey: socialKeys.feed(clubId) });
  }, [clubId, queryClient]);

  const handleCheckHandle = useCallback(async (handle: string) => {
    const res = await checkUserHandleAvailable(handle);
    return res.available;
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return members;
    const q = search.toLowerCase();
    return members.filter(
      (m) => m.user?.full_name?.toLowerCase().includes(q) || m.club_role.toLowerCase().includes(q),
    );
  }, [members, search]);

  // Find the current user's membership for the "Your Profile" card
  const myMembership = useMemo(
    () => members.find((m) => m.user?.id === currentUserId),
    [members, currentUserId],
  );

  const displayName = userProfile?.display_name || authProfile?.display_name || myMembership?.user?.full_name || 'You';
  const avatarUrl = userProfile?.avatar_url || myMembership?.user?.avatar_url;

  return (
    <div className="space-y-4">
      {/* Your Profile card */}
      {currentUserId && (
        <div
          className="rounded-xl p-4"
          style={{
            backgroundColor: `${theme.colors.primary}08`,
            border: `1px solid ${theme.colors.primary}25`,
          }}
        >
          <div className="flex items-center gap-3">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-12 w-12 rounded-full object-cover" />
            ) : (
              <div
                className="flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold"
                style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
              >
                {(displayName[0] || '?').toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold truncate" style={{ color: theme.colors.text }}>{displayName}</span>
                {myMembership && <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: theme.colors.primary }}>{myMembership.club_role}</span>}
              </div>
              {userProfile?.tagline ? (
                <p className="text-xs truncate" style={{ color: theme.colors.muted }}>{userProfile.tagline}</p>
              ) : (
                <p className="text-xs" style={{ color: theme.colors.muted }}>Add a tagline to introduce yourself</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowEditor(!showEditor)}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold transition-colors"
              style={{
                backgroundColor: showEditor ? theme.colors.primary : `${theme.colors.primary}15`,
                color: showEditor ? theme.colors.bg : theme.colors.primary,
              }}
            >
              <PencilSquareIcon className="h-3.5 w-3.5" />
              {showEditor ? 'Close' : 'Edit Profile'}
            </button>
          </div>

          {/* Inline profile editor */}
          {showEditor && userProfile && (
            <div className="mt-4 rounded-xl p-4" style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.muted}20` }}>
              <ProfileEditor
                profile={userProfile as UserProfileData}
                role={authProfile?.role}
                onSave={handleSaveProfile}
                onCheckHandle={handleCheckHandle}
              />
            </div>
          )}

          {showEditor && profileLoading && (
            <div className="mt-4 flex justify-center py-6">
              <div
                className="h-5 w-5 animate-spin rounded-full border-2 border-t-transparent"
                style={{ borderColor: `${theme.colors.muted}40`, borderTopColor: theme.colors.primary }}
              />
            </div>
          )}
        </div>
      )}

      {/* Search bar */}
      <div className="relative">
        <MagnifyingGlassIcon
          className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
          style={{ color: theme.colors.muted }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search members..."
          className="w-full rounded-xl py-2 pl-9 pr-3 text-sm outline-none"
          style={{
            backgroundColor: `${theme.colors.muted}15`,
            color: theme.colors.text,
            border: `1px solid ${theme.colors.muted}30`,
          }}
        />
      </div>

      {/* Count */}
      <p className="text-xs" style={{ color: theme.colors.muted }}>
        {filtered.length} member{filtered.length !== 1 ? 's' : ''}
        {search && ` matching "${search}"`}
      </p>

      {/* Loading */}
      {isLoading && (
        <div className="py-8 text-center">
          <div
            className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-t-transparent"
            style={{ borderColor: `${theme.colors.muted}40`, borderTopColor: theme.colors.primary }}
          />
        </div>
      )}

      {/* Members grid */}
      {!isLoading && (
        <div className="grid gap-2 sm:grid-cols-2">
          {filtered.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              clubId={clubId}
              currentUserId={currentUserId}
              theme={theme}
              onMessage={onMessageUser}
              onViewProfile={onViewMember}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filtered.length === 0 && (
        <div
          className="rounded-xl p-6 text-center"
          style={{ backgroundColor: theme.colors.surface, border: `1px dashed ${theme.colors.muted}40` }}
        >
          <p className="text-sm" style={{ color: theme.colors.muted }}>
            {search ? 'No members match your search.' : 'No members yet.'}
          </p>
        </div>
      )}
    </div>
  );
}
